import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Activity,
  Layers,
  Pill,
  Atom,
  Calculator,
  Syringe,
  Pencil,
  X,
  Edit3,
  Microscope,
  FlaskConical,
  Hexagon,
} from 'lucide-react';
import { StructureEditorModal } from '../../components/chemistry/StructureEditor';
import WorkbenchTab from '../../components/chemistry/compound-tabs/WorkbenchTab';
import { wbHubs, wbCategories, getWbHubsByCategory, getActiveWbHubs, getHubProxyUrl, type WbHubEntry } from '../../config/hubRegistry';
import { useFeatureStore, resolveHubModKey } from '../../stores/featureStore';

interface EndpointInfo {
  method: string;
  path: string;
  desc: string;
  hub: string;
}

const iconBox = (icon: React.ReactNode) => (
  <div
    style={{
      width: 40,
      height: 40,
      borderRadius: 10,
      background: 'rgba(13, 148, 136, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {icon}
  </div>
);


const allEndpoints: EndpointInfo[] = [
  { method: 'POST', path: '/v2/admet/predict', desc: 'ADMET prediction (druglikeness, absorption, BBB, solubility)', hub: 'ADMET & Safety' },
  { method: 'POST', path: '/v2/properties/calculate', desc: 'Physicochemical properties (MW, LogP, TPSA, ...)', hub: 'ADMET & Safety' },
  { method: 'POST', path: '/v2/toxicophores/scan', desc: 'PAINS / Brenk / NIH structural alert scan', hub: 'ADMET & Safety' },
  { method: 'POST', path: '/v2/metabolism/liability', desc: 'Metabolic liability & soft spot prediction', hub: 'Metabolism' },
  { method: 'POST', path: '/v2/metabolism/predict', desc: 'Phase I metabolite prediction (CYP oxidation)', hub: 'Metabolism' },
  { method: 'POST', path: '/v2/metabolism/secondary', desc: 'Phase II conjugation (glucuronidation, sulfation)', hub: 'Metabolism' },
  { method: 'POST', path: '/v2/metabolism/full', desc: 'Complete metabolism analysis (prediction + liability)', hub: 'Metabolism' },
  { method: 'GET', path: '/v2/metabolism/cyp-presets', desc: 'List CYP isoform docking presets', hub: 'Metabolism' },
  { method: 'POST', path: '/v2/metabolism/cyp-dock', desc: 'CYP active-site docking (AutoDock Vina)', hub: 'Metabolism' },
  { method: 'POST', path: '/v2/conformers/generate', desc: '3D conformer generation (MMFF94/UFF)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/homo-lumo/calculate', desc: 'HOMO/LUMO orbital energies (PySCF)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/iupac/name', desc: 'SMILES to IUPAC name (STOUT)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/descriptors/substructure', desc: 'Substructure descriptors (amines, amides, ...)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/docking/dock', desc: 'Molecular docking (AutoDock Vina)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/props/topology', desc: 'Topology descriptors (atoms, rings, stereo)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/props/shape', desc: '3D shape descriptors (PMI, asphericity)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/fingerprints/calculate', desc: 'Molecular fingerprints (Morgan, MACCS)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/convert/format', desc: 'Format conversion (SMILES to InChI, SDF)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/name-to-structure', desc: 'Chemical name to SMILES (OPSIN)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/charges/calculate', desc: 'Partial atomic charges (AM1-BCC, Gasteiger)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/descriptors/padel', desc: 'PaDEL molecular descriptors (1400+ 2D)', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/descriptors/extended', desc: 'RDKit molecular descriptors (~200)', hub: 'Molecular Analysis' },
  { method: 'GET', path: '/v2/formats/openbabel', desc: 'Supported OpenBabel file formats', hub: 'Molecular Analysis' },
  { method: 'GET', path: '/v2/forcefields/openmm', desc: 'Available OpenMM forcefields', hub: 'Molecular Analysis' },
  { method: 'POST', path: '/v2/mpo/score', desc: 'Multi-parameter optimization scoring (radar chart)', hub: 'Drug Design' },
  { method: 'POST', path: '/v2/library/enumerate', desc: 'Combinatorial library enumeration', hub: 'Library Design' },
  { method: 'POST', path: '/v2/library/depict', desc: 'Molecule grid depiction (SVG)', hub: 'Library Design' },
  { method: 'POST', path: '/v2/library/properties', desc: 'Library compound properties', hub: 'Library Design' },
  { method: 'POST', path: '/v2/kinetics/fit/ic50', desc: 'IC50 dose-response fitting (4PL)', hub: 'Kinetics & Binding' },
  { method: 'POST', path: '/v2/kinetics/fit/mm', desc: 'Michaelis-Menten fitting', hub: 'Kinetics & Binding' },
  { method: 'POST', path: '/v2/kinetics/fit/scatchard', desc: 'Scatchard plot fitting', hub: 'Kinetics & Binding' },
  { method: 'POST', path: '/v2/kinetics/fit/rosenthal', desc: 'Rosenthal plot fitting', hub: 'Kinetics & Binding' },
  { method: 'POST', path: '/v2/kinetics/fit/klotz', desc: 'Klotz plot fitting', hub: 'Kinetics & Binding' },
  { method: 'POST', path: '/v2/kinetics/fit/spr', desc: 'SPR sensorgram fitting', hub: 'Kinetics & Binding' },
  { method: 'POST', path: '/patents/collision-map/smiles', desc: 'Patent collision search (Tanimoto)', hub: 'Patent & IP' },
  { method: 'GET', path: '/v2/calcs/equations', desc: 'List all 63 equations (11 domains)', hub: 'Equation Solver' },
  { method: 'GET', path: '/v2/calcs/equations/{id}', desc: 'Get equation details and variables', hub: 'Equation Solver' },
  { method: 'POST', path: '/v2/calcs/solve', desc: 'Symbolic equation solving (SymPy + Pint)', hub: 'Equation Solver' },
  { method: 'GET', path: '/v2/pkpd/models', desc: 'List PK/PD model examples', hub: 'PK/PD Modelling' },
  { method: 'POST', path: '/v2/pkpd/run', desc: 'Run PK/PD simulation (mrgsolve)', hub: 'PK/PD Modelling' },
  { method: 'POST', path: '/v2/minimize', desc: 'Energy minimisation (OpenMM)', hub: 'Molecular Analysis' },
  { method: 'GET', path: '/v2/pkpd/artifacts/{name}', desc: 'Download PK/PD simulation artifact', hub: 'PK/PD Modelling' },
];

const JUMP_CATEGORIES = wbCategories;

const WorkbenchLanding: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [drawnSmiles, setDrawnSmiles] = useState<string | null>(null);
  const [jumpValue, setJumpValue] = useState('');

  const { isModuleEnabled } = useFeatureStore();
  const isHubEnabled = (h: WbHubEntry) => {
    const key = resolveHubModKey(h.id, h.category);
    return !key || isModuleEnabled(key);
  };

  const activeHubs = useMemo(() => getActiveWbHubs(), []);

  const toolsByCategory = useMemo(() => {
    const grouped: Record<string, WbHubEntry[]> = {};
    for (const cat of JUMP_CATEGORIES) {
      const list = activeHubs
        .filter((h) => h.category === cat && isHubEnabled(h))
        .sort((a, b) => a.name.localeCompare(b.name));
      if (list.length > 0) grouped[cat] = list;
    }
    return grouped;
  }, [activeHubs, isModuleEnabled]);

  const handleJump = (hubId: string) => {
    if (!hubId) return;
    setJumpValue('');
    const el = document.getElementById(`tool-tile-${hubId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.outline = '2px solid #0D9488';
      setTimeout(() => { el.style.outline = 'none'; }, 1500);
    } else {
      navigate(`/workbench/${hubId}`);
    }
  };

  const filteredEndpoints = useMemo(() => {
    if (!search.trim()) return allEndpoints;
    const q = search.toLowerCase();
    return allEndpoints.filter(
      (ep) =>
        ep.desc.toLowerCase().includes(q) ||
        ep.hub.toLowerCase().includes(q)
    );
  }, [search]);

  const groupedEndpoints = useMemo(() => {
    const groups: Record<string, EndpointInfo[]> = {};
    filteredEndpoints.forEach((ep) => {
      if (!groups[ep.hub]) groups[ep.hub] = [];
      groups[ep.hub].push(ep);
    });
    return groups;
  }, [filteredEndpoints]);

  const totalEndpoints = allEndpoints.length;
  const matchingCount = filteredEndpoints.length;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Toolbox</span>
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 6,
              background: 'rgba(245,158,11,0.15)',
              color: '#f59e0b',
              fontWeight: 600,
              fontFamily: 'monospace',
            }}
          >
            {getActiveWbHubs().length} active hubs
          </span>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
          Computational chemistry workflows powered by live microservices
        </p>
      </div>

      {/* Controls bar: Jump dropdown + Search */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
        <select
          value={jumpValue}
          onChange={(e) => handleJump(e.target.value)}
          style={{
            padding: '10px 14px',
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 10,
            color: 'var(--text-primary)',
            fontSize: 14,
            outline: 'none',
            minWidth: 200,
            cursor: 'pointer',
          }}
        >
          <option value="">Jump to tool…</option>
          {JUMP_CATEGORIES.map((cat) => {
            const list = toolsByCategory[cat];
            if (!list) return null;
            return (
              <optgroup key={cat} label={cat}>
                {list.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            );
          })}
        </select>

        <div style={{ flex: 1, position: 'relative' }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ position: 'absolute', left: 14, top: 12, pointerEvents: 'none' }}
          >
            <circle cx="7" cy="7" r="5" stroke="var(--text-secondary)" strokeWidth="1.5" />
            <path d="M11 11L14 14" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tools..."
            style={{
              width: '100%',
              padding: '10px 14px 10px 38px',
              background: 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: 10,
              color: 'var(--text-primary)',
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Draw Structure Card */}
      {!drawnSmiles ? (
        <button
          onClick={() => setShowEditor(true)}
          style={{
            background: 'transparent',
            border: '2px dashed var(--card-border)',
            borderRadius: 14,
            padding: '16px 20px',
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            transition: 'all 200ms ease',
            width: '100%',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = '#0D9488';
            (e.currentTarget as HTMLElement).style.background = 'rgba(13,148,136,0.04)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--card-border)';
            (e.currentTarget as HTMLElement).style.background = 'transparent';
          }}
        >
          {iconBox(<Pencil size={22} color="#0D9488" />)}
          <div>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Draw Structure</span>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '2px 0 0 0' }}>
              Sketch a molecule and run any toolbox tool on it
            </p>
          </div>
        </button>
      ) : (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 14,
          marginBottom: 16,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid var(--card-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {iconBox(<Pencil size={18} color="#0D9488" />)}
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Drawn Structure</span>
                <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)', marginLeft: 8 }}>
                  {drawnSmiles.length > 50 ? drawnSmiles.slice(0, 50) + '...' : drawnSmiles}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => setShowEditor(true)}
                style={{
                  background: 'rgba(13,148,136,0.1)',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 10px',
                  fontSize: 11,
                  fontWeight: 500,
                  color: '#0D9488',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Edit3 size={11} /> Edit
              </button>
              <button
                onClick={() => setDrawnSmiles(null)}
                style={{
                  background: 'rgba(220,38,38,0.06)',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <X size={11} /> Clear
              </button>
            </div>
          </div>
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            <WorkbenchTab smiles={drawnSmiles} />
          </div>
        </div>
      )}

      {/* Structure Editor Modal */}
      <StructureEditorModal
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={(_molfile, smiles) => {
          setDrawnSmiles(smiles);
          setShowEditor(false);
        }}
        initialStructure={drawnSmiles || ''}
        title="Draw Structure"
      />

      <HubDirectory search={search} />

      {/* Show all endpoints */}
      <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 16 }}>
        <button
          onClick={() => setShowAll(!showAll)}
          style={{
            background: 'none',
            border: 'none',
            color: '#f59e0b',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: 0,
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {search.trim() ? `Show ${matchingCount} of ${totalEndpoints} matching tools` : `Show all ${totalEndpoints} tools individually`}
        </button>

        {(showAll || search.trim()) && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {Object.entries(groupedEndpoints).map(([hub, endpoints]) => (
              <div key={hub}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#a7abb6',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 8,
                  }}
                >
                  {hub}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {endpoints.map((ep, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '6px 10px',
                        background: 'var(--hover-bg)',
                        borderRadius: 8,
                        fontSize: 13,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {ep.desc}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--card-border)', display: 'flex', justifyContent: 'center' }}>
        <Link
          to="/workbench/about"
          style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#f59e0b'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--text-muted)'; }}
        >
          Technology Stack &middot; {wbHubs.length} hubs
        </Link>
      </div>
    </div>
  );
};

/* ─── Hub Directory ─── */

const categoryIcons: Record<string, React.ReactNode> = {
  'Chemistry':          <FlaskConical size={14} color="#0D9488" />,
  'Biology & Assays':   <Microscope size={14} color="#0D9488" />,
  'ADMET & Safety':     <Pill size={14} color="#0D9488" />,
  'Pharmacology':       <Syringe size={14} color="#0D9488" />,
  'Genomics & Omics':   <Atom size={14} color="#0D9488" />,
  'Biologics':          <Hexagon size={14} color="#0D9488" />,
  'Computational':      <Calculator size={14} color="#0D9488" />,
  'Clinical':           <Activity size={14} color="#0D9488" />,
  'Platform':           <Layers size={14} color="#0D9488" />,
};

const HubCard: React.FC<{ hub: WbHubEntry; navigate: ReturnType<typeof useNavigate> }> = ({ hub, navigate }) => {
  const errored = hub.status === 'error';
  const disabled = hub.comingSoon === true || errored;
  // Until a real workload has completed through the deployed UI, keep the
  // tool visibly marked as beta even when its page and container respond.
  const showBeta = !disabled && hub.endToEndTested !== true;
  return (
    <button
      id={`tool-tile-${hub.id}`}
      key={hub.id}
      onClick={() => !disabled && hub.port && window.open(getHubProxyUrl(hub), '_blank', 'noopener,noreferrer')}
      disabled={disabled}
      style={{
        position: 'relative',
        background: disabled ? 'var(--hover-bg)' : 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 10,
        padding: '14px 14px',
        cursor: disabled ? 'default' : 'pointer',
        textAlign: 'left',
        transition: 'all 200ms ease',
        opacity: disabled || errored ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          (e.currentTarget as HTMLElement).style.borderColor = '#0D9488';
          (e.currentTarget as HTMLElement).style.background = 'var(--hover-bg)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          (e.currentTarget as HTMLElement).style.borderColor = 'var(--card-border)';
          (e.currentTarget as HTMLElement).style.background = disabled ? 'var(--hover-bg)' : 'var(--card-bg)';
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {hub.name}
        </span>
        {errored ? (
          <span
            style={{
              fontSize: 9,
              padding: '1px 5px',
              borderRadius: 4,
              background: 'rgba(220,38,38,0.15)',
              color: '#dc2626',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Offline
          </span>
        ) : hub.comingSoon && (
          <span
            style={{
              fontSize: 9,
              padding: '1px 5px',
              borderRadius: 4,
              background: 'rgba(156,163,175,0.2)',
              color: 'var(--text-muted)',
              fontWeight: 600,
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            Coming soon
          </span>
        )}
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: 0, lineHeight: 1.35 }}>
        {hub.description}
      </p>
      {showBeta && (
        <span
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            fontSize: 9,
            padding: '1px 5px',
            borderRadius: 4,
            background: 'var(--warning-500)',
            color: '#fff',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Beta
        </span>
      )}
    </button>
  );
};

const HubSection: React.FC<{
  title: string;
  badge: string;
  badgeBg: string;
  badgeColor: string;
  grouped: Record<string, WbHubEntry[]>;
  navigate: ReturnType<typeof useNavigate>;
}> = ({ title, badge, badgeBg, badgeColor, grouped, navigate }) => {
  const count = Object.values(grouped).reduce((n, list) => n + list.length, 0);
  if (count === 0) return null;
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        <span
          style={{
            fontSize: 11,
            padding: '2px 8px',
            borderRadius: 6,
            background: badgeBg,
            color: badgeColor,
            fontWeight: 600,
            fontFamily: 'monospace',
          }}
        >
          {count} {badge}
        </span>
      </div>
      {wbCategories.map((cat) => {
        const list = grouped[cat];
        if (!list || list.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: 20 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#a7abb6',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {categoryIcons[cat]}
              {cat}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 10,
              }}
            >
              {list.map((hub) => (
                <HubCard key={hub.id} hub={hub} navigate={navigate} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const HubDirectory: React.FC<{ search: string }> = ({ search }) => {
  const navigate = useNavigate();
  const { isModuleEnabled } = useFeatureStore();

  const allGrouped = useMemo(() => getWbHubsByCategory(), []);
  const grouped = useMemo(() => {
    const result = {} as Record<string, WbHubEntry[]>;
    for (const [cat, list] of Object.entries(allGrouped)) {
      const filtered = list.filter((h) => {
        const key = resolveHubModKey(h.id, h.category);
        return !key || isModuleEnabled(key);
      });
      if (filtered.length > 0) result[cat] = filtered;
    }
    return result;
  }, [allGrouped, isModuleEnabled]);

  const filterBySearch = (list: WbHubEntry[]) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (h) =>
        h.name.toLowerCase().includes(q) ||
        h.description.toLowerCase().includes(q) ||
        h.id.toLowerCase().includes(q)
    );
  };

  const isToolbox = (h: WbHubEntry) =>
    h.featured === true && h.status === 'active' && !h.comingSoon;

  const toolboxGrouped = useMemo(() => {
    const result = {} as Record<string, WbHubEntry[]>;
    for (const [cat, list] of Object.entries(grouped)) {
      const filtered = filterBySearch(list.filter(isToolbox));
      if (filtered.length > 0) result[cat] = filtered;
    }
    return result;
  }, [search, grouped]);

  const otherGrouped = useMemo(() => {
    const result = {} as Record<string, WbHubEntry[]>;
    for (const [cat, list] of Object.entries(grouped)) {
      const filtered = filterBySearch(list.filter((h) => !isToolbox(h)));
      if (filtered.length > 0) result[cat] = filtered;
    }
    return result;
  }, [search, grouped]);

  return (
    <>
      <HubSection
        title="Toolbox"
        badge="hubs"
        badgeBg="rgba(13,148,136,0.1)"
        badgeColor="#0D9488"
        grouped={toolboxGrouped}
        navigate={navigate}
      />
      <HubSection
        title="Beta Tools"
        badge="hubs"
        badgeBg="rgba(13,148,136,0.08)"
        badgeColor="#0D9488"
        grouped={otherGrouped}
        navigate={navigate}
      />
    </>
  );
};

export default WorkbenchLanding;
